import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chunk } from 'lodash-es';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import DownloadButton from 'components/stateless/Button/DownloadButton';
import { FormBorder } from 'components/stateless/CommonForm/FormBorder';
import { FormContents } from 'components/stateless/CommonForm/FormContents';
import { BackGroundType, FormSubtitle } from 'components/stateless/CommonForm/FormSubtitle';
import { FormValue } from 'components/stateless/CommonForm/FormValue';
import GuideMessage, { MessageType } from 'components/stateless/GuideMessage/GuideMessage';
import StatusDescriptionModal from 'components/stateless/Modal/common/status/StatusDescriptionModal';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_SY } from 'constants/routes/system';
import { LOAN_STATUS } from 'enums';
import useMounted from 'hooks/useMounted';
import type { InvoiceAdditionalDataVO } from 'models/vo/InvoiceAdditionalDataVO';
import type { SuccessInvoiceDetailVOModel } from 'models/vo/SuccessInvoiceDetailVO';
import getStatusBadgeClass from 'utils/classNames/getStatusBadgeClass';
import {
  requestSystemDownloadScannedInvoice,
  requestSystemSuccessInvoiceDetail,
} from 'utils/http/api/system/success-invoices';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { isLoanDisbursed } from 'utils/status';
import { tableValueManage } from 'utils/valueManager/ValueManager';

function SystemInvoiceDetail() {
  const mounted = useMounted();
  const history = useHistory();
  const modal = useModal();
  const { t } = useTranslation(['format']);
  const { successInvoiceId } = useParams<any>();
  const [successInvoiceDetailData, setSuccessInvoiceDetailData] = useState<SuccessInvoiceDetailVOModel>();

  useEffect(() => {
    if (mounted) {
      fetchAll();
    }
  }, [mounted]);

  const fetchAll = async (): Promise<void> => {
    try {
      const successInvoiceDetailData = await requestSystemSuccessInvoiceDetail(successInvoiceId);

      ReactDOM.unstable_batchedUpdates(() => {
        setSuccessInvoiceDetailData(successInvoiceDetailData);
      });
    } catch (error) {
      modal.show(error);
    }
  };

  const onClickLoanStatus = (): void => {
    modal.show(
      <StatusDescriptionModal
        statusDescriptionEnum={Object.values(LOAN_STATUS)}
        statusDescriptionModalType="BOX"
        statusDescriptionEnumType="LOAN_STATUS"
      />,
      {
        modalSize: ModalSize.XL,
        modalType: ModalType.ALERT,
        closeBtnText: t('text:Close'),
      },
    );
  };

  const onClickDownload = async (e: any): Promise<void> => {
    e.preventDefault();

    try {
      requestSystemDownloadScannedInvoice(successInvoiceId);
    } catch (error) {
      modal.show(error);
    }
  };

  const renderGuideMessage = (): JSX.Element | undefined => {
    if (successInvoiceDetailData?.blockedByFinancier) {
      return (
        <GuideMessage
          messageType={MessageType.ALERT}
          message={[t('text:The_Invoice_has_been_blocked'), t('text:Please_check_the_reason_for_block_below')]}
          reason={successInvoiceDetailData.blockedReason}
          reasonTitle={t('text:REASON_FOR_BLOCK')}
        />
      );
    }
  };

  return (
    <>
      <BackHeaderTitle title={t('text:Invoice_Details')} />
      {renderGuideMessage()}
      <div className="content-area">
        <form>
          <SectionTitle title={t('text:Registered_Invoice_Summary')} />
          <FormBorder>
            <FormContents backGroundType={BackGroundType.WHITE}>
              <div className="row">
                <FormValue
                  col={3}
                  className="information-form__input border-none pointfont"
                  label={t('text:Invoice_Amount')}
                  value={tableValueManage(
                    successInvoiceDetailData?.invoiceAmount,
                    t('format:number', { value: successInvoiceDetailData?.invoiceAmount }),
                  )}
                />
                <FormValue
                  col={3}
                  label={t('text:Payment_Date')}
                  value={tableValueManage(
                    successInvoiceDetailData?.settlementDate,
                    t('format:date', { value: successInvoiceDetailData?.settlementDate, key: 'datetime' }),
                  )}
                />
                <FormValue
                  col={3}
                  label={t('text:Payment_Completion_Date')}
                  value={tableValueManage(
                    successInvoiceDetailData?.disbursedDate,
                    t('format:date', { value: successInvoiceDetailData?.disbursedDate, key: 'datetime' }),
                  )}
                />
                <FormValue
                  col={3}
                  label={t('text:Payment_Status')}
                  value={tableValueManage(
                    successInvoiceDetailData?.successInvoiceStatus,
                    t(`code:success-invoice-status.${successInvoiceDetailData?.successInvoiceStatus}`),
                  )}
                />
              </div>
            </FormContents>
            <FormContents>
              <div className="row">
                <FormValue
                  col={3}
                  label={t('text:Invoice_Number')}
                  value={tableValueManage(successInvoiceDetailData?.invoiceNumber)}
                />
                <FormValue
                  col={3}
                  label={t('text:Invoice_Issued_Date')}
                  value={successInvoiceDetailData?.invoiceIssuedDate}
                  format="date"
                />
                <FormValue
                  col={3}
                  label={t('text:Currency')}
                  value={tableValueManage(successInvoiceDetailData?.currencyType)}
                />
                <FormValue
                  col={3}
                  label={t('text:Financing_Disbursement_Status')}
                  value={isLoanDisbursed(successInvoiceDetailData?.loanStatus) ? t('text:Yes') : t('text:No')}
                />
              </div>
              <div className="row">
                <FormValue
                  col={3}
                  label={t('text:Financier_Name')}
                  value={tableValueManage(successInvoiceDetailData?.financierName)}
                />
                <FormValue
                  col={3}
                  label={t('text:Anchor_Name')}
                  value={tableValueManage(successInvoiceDetailData?.anchorClientName)}
                />
                <FormValue
                  col={3}
                  label={t('text:Partner_Name')}
                  value={tableValueManage(successInvoiceDetailData?.dealerClientName)}
                />
              </div>
            </FormContents>
          </FormBorder>
        </form>
      </div>

      {(successInvoiceDetailData?.invoiceAdditionalDataList?.length ?? 0) > 0 && (
        <div className="content-area">
          <SectionTitle title={t('text:Invoice_Details')} />
          <FormBorder>
            <FormContents backGroundType={BackGroundType.Gray}>
              {chunk(successInvoiceDetailData?.invoiceAdditionalDataList, 2).map(
                (chunkedTwoAdditionalData: InvoiceAdditionalDataVO[], i) => (
                  <div key={i} className="row">
                    {chunkedTwoAdditionalData.map((additionalData: InvoiceAdditionalDataVO, l) => (
                      <FormValue
                        key={`${additionalData.name}-${additionalData.value}-${l}`}
                        label={additionalData.name}
                        value={additionalData.value}
                      />
                    ))}
                  </div>
                ),
              )}
            </FormContents>
          </FormBorder>
        </div>
      )}

      <div className="content-area">
        <SectionTitle title={t('text:Financing_Information')}>
          {successInvoiceDetailData?.loanId ? (
            <div className="flex-end align-items-center me-3">
              <span className="me-2">{t('text:Go_to_Financing_Details')}</span>
              <Button
                size={ButtonSizeEnum.SM}
                variant={ButtonVariantEnum.OUTLINED}
                color={ButtonColorEnum.SECONDARY}
                className="flex-column-center"
                onClick={() => {
                  history.push(
                    ROUTES_SY.VIEW_TRANSACTION.FINANCING_DETAIL_BUILD_PATH(successInvoiceDetailData?.loanId!),
                  );
                }}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          ) : null}
        </SectionTitle>
        <FormBorder>
          <FormContents>
            <div className="row">
              <FormValue
                col={3}
                label={t('text:Disbursed_Date')}
                value={successInvoiceDetailData?.disbursedDate}
                format="date"
              />
              <FormValue
                col={3}
                label={t('text:Scheduled_Repayment_Date')}
                value={successInvoiceDetailData?.repaymentDate}
                format="date"
              />
              <FormValue
                col={3}
                label={t('text:Repaid_Date')}
                value={successInvoiceDetailData?.repaidDate}
                format="date"
              />
              <FormValue
                col={3}
                className={getStatusBadgeClass('LOAN_STATUS', successInvoiceDetailData?.loanStatus)}
                label={t('text:Financing_Status')}
                value={tableValueManage(t(`code:financing-status.${successInvoiceDetailData?.loanStatus}`))}
                hasStatusDescription={true}
                showStatusDescriptionFunc={onClickLoanStatus}
              />
            </div>
          </FormContents>
          <div className="detail-in-file-download-form">
            <div className="d-flex justify-content-between">
              <label className="information-form__label">{t('text:Tax_Invoice_File')}</label>
              <input
                type="text"
                placeholder={t('text:No_file_attached')}
                className="upload-file-input"
                readOnly={true}
                defaultValue={
                  successInvoiceDetailData?.scannedInvoiceAttachmentFileName
                    ? successInvoiceDetailData?.scannedInvoiceAttachmentFileName
                    : ''
                }
              />
              <DownloadButton
                disabled={successInvoiceDetailData?.scannedInvoiceAttachmentId ? false : true}
                onClick={onClickDownload}
              />
            </div>
          </div>
        </FormBorder>
      </div>

      <div className="content-area">
        <SectionTitle title={t('text:Registration_Information')} />
        <FormBorder>
          <FormSubtitle title={t('text:Registration_Confirmation')} backGroundType={BackGroundType.DarkGray} />
          <FormContents>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Enterprise_Type')}
                value={t(`code:enterprise-type.${successInvoiceDetailData?.currentUploadEnterpriseType}`)}
              />
              <FormValue
                className="information-form__input"
                label={t('text:Enterprise_Name')}
                value={successInvoiceDetailData?.currentConfirmEnterpriseName}
              />
            </div>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Person_in_Charge')}
                value={successInvoiceDetailData?.currentConfirmUserName}
              />
              <FormValue
                className="information-form__input"
                label={t('text:Authority')}
                value={t(`code:authority-type.${successInvoiceDetailData?.currentUploadUserAuthorityType}`)}
                format="code"
              />
            </div>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Registered_Time')}
                value={successInvoiceDetailData?.currentInvoiceApprovalDateTime}
                format="datetime"
              />
            </div>
          </FormContents>
          <FormSubtitle title={t('text:Registration_Request')} backGroundType={BackGroundType.DarkGray} />
          <FormContents>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Enterprise_Type')}
                value={t(`code:enterprise-type.${successInvoiceDetailData?.initialUploadEnterpriseType}`)}
              />
              <FormValue
                className="information-form__input"
                label={t('text:Enterprise_Name')}
                value={successInvoiceDetailData?.initialUploadEnterpriseName}
              />
            </div>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Person_in_Charge')}
                value={successInvoiceDetailData?.initialUploadUserName}
              />
              <FormValue
                className="information-form__input"
                label={t('text:Authority')}
                value={t(`code:authority-type.${successInvoiceDetailData?.initialUploadUserAuthorityType}`)}
                format="code"
              />
            </div>
            <div className="row">
              <FormValue
                className="information-form__input"
                label={t('text:Registered_Time')}
                value={successInvoiceDetailData?.initialInvoiceApprovalDateTime}
                format="datetime"
              />
            </div>
          </FormContents>
        </FormBorder>
      </div>
    </>
  );
}

export default SystemInvoiceDetail;
